import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Search } from 'lucide-react'
import { listProducts, getTenantInfo } from '../api/products'
import { useLangPrefix } from '../hooks/useLangPrefix'
import { createOrder } from '../api/orders'
import DesktopShell from '../components/layout/DesktopShell'
import AppShell from '../components/layout/AppShell'
import useManagerStore from '../store/managerStore'
import * as Sentry from '@sentry/react'
import { formatMoney, getMinorUnits, parseMoneyInput } from '../lib/money'
import { formatAmountForInput, moneyApiErrorMessage, moneyInputErrorMessage } from '../lib/moneyInput'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'

const ORDER_TYPES = {
    fnb:         'table_order',
    spa:         'appointment',
    beauty:      'appointment', // legacy alias for spa
    hotel:       'room_order',
    travel:      'tour_booking',
    education:   'enrollment',
    retail:      'appointment',
    other:       'appointment',
}

const PAYMENT_METHODS = ['cash', 'card', 'bank_transfer', 'momo', 'zalo_pay', 'other']

const MAX_PERCENT_INPUT = 100
// Money inputs keep the raw typed text (digits and separators only) and are
// parsed with parseMoneyInput in the tenant currency (SPEC-growchat-tenant-currency.md,
// Design 8). Amounts are decimal major units: never convert with `* 100`.
const keepMoneyChars = (value) => String(value || '').replace(/[^\d.,]/g, '')
/** Parsed amount for live totals; empty or invalid text counts as 0. */
const amountOf = (text, currency) => {
    const result = parseMoneyInput(text, currency)
    return result.ok && result.value !== null ? result.value : 0
}
/** Round a computed amount to the currency's decimals (drops float drift like 12.299999999999999). */
const toCurrencyScale = (value, currency) => Number(Number(value).toFixed(getMinorUnits(currency)))
const normalizePercentInput = (value) => {
    const normalized = String(value || '')
        .replace(',', '.')
        .replace(/[^\d.]/g, '')
    if (!normalized) return ''
    const [intPart = '', ...decimalParts] = normalized.split('.')
    const merged = decimalParts.length > 0 ? `${intPart}.${decimalParts.join('')}` : intPart
    const parsed = Number(merged)
    if (!Number.isFinite(parsed)) return ''
    return String(Math.min(parsed, MAX_PERCENT_INPUT))
}
const parsePercentInput = (value) => {
    if (value === '') return 0
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return 0
    return Math.min(Math.max(parsed, 0), MAX_PERCENT_INPUT)
}
const getProductSku = (product) => product?.sku_code || product?.sku || ''
const getProductSellingPrice = (product) => {
    const candidates = [
        product?.final_display_price,
        product?.pricing?.sale_price,
        product?.pricing?.base_price,
        product?.price,
    ]
    for (const candidate of candidates) {
        const parsed = Number(candidate)
        if (Number.isFinite(parsed) && parsed >= 0) return parsed
    }
    return 0
}

export default function OrderFormPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { prefix, lang } = useLangPrefix()
    const tenantId = useManagerStore((s) => s.tenantId)
    const isDesktop = window.innerWidth >= 1024
    // New orders are priced in the tenant currency; money inputs and amounts
    // wait for it to resolve (unresolved means the VND placeholder).
    const { currency, isResolved: currencyResolved } = useTenantCurrencyState()
    const currencyLabel = currencyResolved ? currency : '…'
    const hasDecimals = getMinorUnits(currency) > 0
    const formatPrice = (amount) => (currencyResolved
        ? formatMoney(amount || 0, currency, lang)
        : <span role="status" aria-label={t('common.loading')}>…</span>)

    // Customer
    const [customerName, setCustomerName] = useState('')
    const [customerPhone, setCustomerPhone] = useState('')
    const [customerEmail, setCustomerEmail] = useState('')

    // Order meta
    const [tenantIndustry, setTenantIndustry] = useState('fnb')
    const [tableNumber, setTableNumber] = useState('')
    const [partySize, setPartySize] = useState('')
    const [scheduledAt, setScheduledAt] = useState('')

    // Line items
    const [lineItems, setLineItems] = useState([])
    const [products, setProducts] = useState([])
    const [productSearch, setProductSearch] = useState('')
    const [showProductDropdown, setShowProductDropdown] = useState(false)
    const productSearchRef = useRef(null)

    // Pricing
    const [discountType, setDiscountType] = useState('none')
    const [discountValue, setDiscountValue] = useState('')
    const [taxPercent, setTaxPercent] = useState('')
    const [shippingFee, setShippingFee] = useState('')

    // Payment
    const [paymentMethod, setPaymentMethod] = useState('cash')
    const [paymentStatus, setPaymentStatus] = useState('unpaid')
    const [depositAmount, setDepositAmount] = useState('')

    // Notes
    const [internalNote, setInternalNote] = useState('')

    // Custom item entry
    const [showCustomItemForm, setShowCustomItemForm] = useState(false)
    const [customItemName, setCustomItemName] = useState('')
    const [customItemPrice, setCustomItemPrice] = useState('')

    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    // Inline money input errors keyed by field: customItem, discount, shipping, deposit.
    const [moneyErrors, setMoneyErrors] = useState({})

    const amountErrorMessage = (errorCode) => moneyInputErrorMessage(t, errorCode, currency, lang)

    /** Validate one money field; records its inline error and returns the parse result. */
    function validateAmount(field, text) {
        const result = parseMoneyInput(text, currency)
        setMoneyErrors(prev => ({ ...prev, [field]: result.ok ? null : amountErrorMessage(result.error) }))
        return result
    }

    /** onChange/onBlur handlers for a raw-text money input; blur shows canonical text. */
    function moneyInputHandlers(field, text, setText) {
        return {
            onChange: e => {
                setText(keepMoneyChars(e.target.value))
                setMoneyErrors(prev => ({ ...prev, [field]: null }))
            },
            onBlur: () => {
                const result = validateAmount(field, text)
                if (result.ok && result.value !== null) setText(formatAmountForInput(result.value, currency))
            },
            disabled: !currencyResolved,
            inputMode: hasDecimals ? 'decimal' : 'numeric',
            'aria-invalid': Boolean(moneyErrors[field]),
        }
    }

    const moneyError = (field) => moneyErrors[field]
        ? <div role="alert" style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{moneyErrors[field]}</div>
        : null

    useEffect(() => {
        if (!tenantId) {
            setProducts([])
            setTenantIndustry('fnb')
            return
        }
        listProducts(tenantId, { limit: 200 })
            .then(d => setProducts(d.products || []))
            .catch(err => {
                if (!err?.response || err.response.status >= 500)
                    Sentry.captureException(err, { tags: { action: 'load_products' }, extra: { tenantId } })
            })
        getTenantInfo(tenantId)
            .then(d => setTenantIndustry(d.industry_type || 'fnb'))
            .catch(err => {
                if (!err?.response || err.response.status >= 500)
                    Sentry.captureException(err, { tags: { action: 'load_tenant_info' }, extra: { tenantId } })
            })
    }, [tenantId])

    // Click outside to close dropdown
    useEffect(() => {
        function handleClick(e) {
            if (productSearchRef.current && !productSearchRef.current.contains(e.target)) {
                setShowProductDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    const filteredProducts = products.filter(p =>
        !productSearch ||
        p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        getProductSku(p).toLowerCase().includes(productSearch.toLowerCase())
    )

    function addProduct(product) {
        const unitPrice = getProductSellingPrice(product)
        setLineItems(prev => {
            const existing = prev.find(i => i.product_id === product.id)
            if (existing) {
                return prev.map(i => i.product_id === product.id ? { ...i, qty: i.qty + 1 } : i)
            }
            return [...prev, {
                _id: crypto.randomUUID(),
                product_id: product.id,
                product_name: product.name,
                name: product.name,
                category: product.category || '',
                unit_price: unitPrice,
                qty: 1,
                subtotal: unitPrice,
                note: '',
            }]
        })
        setProductSearch('')
        setShowProductDropdown(false)
    }

    function addCustomItem() {
        const name = customItemName.trim()
        if (!name || !currencyResolved) return
        const priceResult = validateAmount('customItem', customItemPrice)
        if (!priceResult.ok) return
        const unitPrice = priceResult.value ?? 0
        setLineItems(prev => [...prev, {
            _id: crypto.randomUUID(),
            product_id: null,
            product_name: name,
            name,
            category: '',
            unit_price: unitPrice,
            qty: 1,
            subtotal: unitPrice,
            note: '',
        }])
        setCustomItemName('')
        setCustomItemPrice('')
        setShowCustomItemForm(false)
    }

    function updateQty(itemId, delta) {
        setLineItems(prev => prev
            .map(i => i._id === itemId ? { ...i, qty: Math.max(1, i.qty + delta) } : i)
        )
    }

    function setQty(itemId, val) {
        const qty = parseInt(val) || 1
        setLineItems(prev => prev.map(i => i._id === itemId ? { ...i, qty: Math.max(1, qty) } : i))
    }

    function removeItem(itemId) {
        setLineItems(prev => prev.filter(i => i._id !== itemId))
    }

    function setItemNote(itemId, note) {
        setLineItems(prev => prev.map(i => i._id === itemId ? { ...i, note } : i))
    }

    const subtotal = lineItems.reduce((s, i) => s + i.unit_price * i.qty, 0)
    const discountPercentValue = parsePercentInput(discountValue)
    const taxPercentValue = parsePercentInput(taxPercent)
    const discountAmt = discountType === 'percent'
        ? subtotal * discountPercentValue / 100
        : discountType === 'fixed'
            ? amountOf(discountValue, currency)
            : 0
    const taxAmt = (subtotal - discountAmt) * taxPercentValue / 100
    const shipping = amountOf(shippingFee, currency)
    const finalTotal = subtotal - discountAmt + taxAmt + shipping
    const maxDepositAmount = Math.max(0, toCurrencyScale(finalTotal, currency))

    useEffect(() => {
        if (depositAmount === '' || !currencyResolved) return
        const parsed = parseMoneyInput(depositAmount, currency)
        if (!parsed.ok || parsed.value === null || parsed.value <= maxDepositAmount) return
        setDepositAmount(maxDepositAmount === 0 ? '' : formatAmountForInput(maxDepositAmount, currency))
    }, [depositAmount, maxDepositAmount, currency, currencyResolved])

    async function handleSubmit() {
        if (!tenantId) {
            setError(t('common.requestFailed'))
            return
        }
        if (!currencyResolved) return
        if (!customerName.trim()) { setError(t('orderForm.errorName')); return }
        if (lineItems.length === 0) { setError(t('orderForm.errorItems')); return }
        if (discountType === 'percent' && Number(discountValue || 0) > MAX_PERCENT_INPUT) {
            setError(t('orderForm.errorDiscountPercentMax'))
            return
        }
        if (Number(taxPercent || 0) > MAX_PERCENT_INPUT) {
            setError(t('orderForm.errorTaxPercentMax'))
            return
        }
        const hasDeposit = paymentStatus === 'deposit_paid' || paymentStatus === 'partial'
        const discountResult = discountType === 'fixed' ? validateAmount('discount', discountValue) : { ok: true, value: null }
        const shippingResult = validateAmount('shipping', shippingFee)
        const depositResult = hasDeposit ? validateAmount('deposit', depositAmount) : { ok: true, value: null }
        const invalid = [discountResult, shippingResult, depositResult].find(r => !r.ok)
        if (invalid) { setError(amountErrorMessage(invalid.error)); return }
        if (hasDeposit && (depositResult.value ?? 0) > maxDepositAmount) {
            setError(t('orderForm.errorDepositMax', { total: formatMoney(maxDepositAmount, currency, lang) }))
            return
        }
        setError('')
        setSaving(true)
        Sentry.addBreadcrumb({ message: 'Create order attempt', level: 'info', data: { tenantId, itemCount: lineItems.length, orderType } })
        try {
            const payload = {
                order_type: orderType,
                industry_type: tenantIndustry,
                channel: 'dashboard',
                customer_name: customerName.trim() || null,
                customer_phone: customerPhone.trim() || null,
                customer_email: customerEmail.trim() || null,
                line_items: lineItems.map(i => ({
                    product_id: i.product_id || null,
                    product_name: i.product_name || i.name || 'Unknown',
                    category: i.category || '',
                    qty: i.qty,
                    // Catalog prices may carry float drift (19.990000000000002): send at currency scale.
                    unit_price: toCurrencyScale(i.unit_price || 0, currency),
                    subtotal: toCurrencyScale((i.qty || 1) * (i.unit_price || 0), currency),
                    attributes: {},
                    notes: i.note || null,
                })),
                order_attributes: {
                    ...(tableNumber ? { table_number: tableNumber } : {}),
                    ...(partySize ? { covers: parseInt(partySize) } : {}),
                    dining_type: 'dine_in',
                },
                discount: discountType !== 'none'
                    ? (discountType === 'fixed' ? (discountResult.value ?? 0) : discountPercentValue)
                    : 0,
                discount_reason: discountType !== 'none' ? discountType : null,
                tax_rate: taxPercentValue / 100,
                service_charge: shippingResult.value ?? 0,
                internal_notes: internalNote?.trim() || null,
            }
            const result = await createOrder(tenantId, payload)
            navigate(`${prefix}/orders/${result.id}`)
        } catch (e) {
            if (!e?.response || e.response.status >= 500)
                Sentry.captureException(e, { tags: { action: 'create_order' }, extra: { tenantId, itemCount: lineItems.length } })
            setError(moneyApiErrorMessage(t, e, currency, lang))
        } finally {
            setSaving(false)
        }
    }

    const orderType = ORDER_TYPES[tenantIndustry] || 'appointment'
    const isFnB = orderType === 'table_order'

    const sectionStyle = {
        background: 'white',
        borderRadius: 14,
        border: '0.5px solid #e0e3f0',
        padding: isDesktop ? '20px 24px' : '16px',
        marginBottom: 16,
    }
    const labelStyle = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6, display: 'block' }
    const inputStyle = { width: '100%', padding: '9px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none', boxSizing: 'border-box' }
    const rowStyle = { display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 14 }

    const content = (
        <div style={{ padding: isDesktop ? '24px 32px' : '12px 12px 80px 12px', background: 'var(--gc-page-bg)', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => navigate(`${prefix}/orders`)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#666', padding: 4 }}>
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <div style={{ fontSize: isDesktop ? 22 : 18, fontWeight: 700, color: '#111' }}>{t('orderForm.title')}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                        {t('orderForm.typeIndustry', { type: orderType.replace(/_/g, ' '), industry: tenantIndustry })}
                    </div>
                </div>
            </div>

            {error && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                    {error}
                </div>
            )}

            {/* Customer Info */}
            <div style={sectionStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('orderForm.customerSection')}</div>
                <div style={rowStyle}>
                    <div>
                        <label style={labelStyle}>{t('orderForm.nameRequired')}</label>
                        <input style={inputStyle} value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder={t('orderForm.namePh')} />
                    </div>
                    <div>
                        <label style={labelStyle}>{t('orderForm.phone')}</label>
                        <input style={inputStyle} value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder={t('orderForm.phonePh')} />
                    </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>{t('orderForm.email')}</label>
                    <input style={inputStyle} value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder={t('orderForm.emailPh')} />
                </div>
                {isFnB && (
                    <div style={rowStyle}>
                        <div>
                            <label style={labelStyle}>{t('orderForm.tableNumber')}</label>
                            <input style={inputStyle} value={tableNumber} onChange={e => setTableNumber(e.target.value)} placeholder={t('orderForm.tablePh')} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('orderForm.partySize')}</label>
                            <input style={inputStyle} type="number" min="1" value={partySize} onChange={e => setPartySize(e.target.value)} placeholder={t('orderForm.partySizePh')} />
                        </div>
                    </div>
                )}
                {!isFnB && (
                    <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>{t('orderForm.scheduledAt')}</label>
                        <input style={inputStyle} type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
                    </div>
                )}
            </div>

            {/* Line Items */}
            <div style={sectionStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('orderForm.items')}</div>

                {/* Product search */}
                <div ref={productSearchRef} style={{ position: 'relative', marginBottom: 10, display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <input
                            style={{ ...inputStyle, paddingLeft: 36 }}
                            value={productSearch}
                            onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true) }}
                            onFocus={() => setShowProductDropdown(true)}
                            placeholder={t('orderForm.searchProductsPh')}
                        />
                        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }}>
                            <Search size={14} />
                        </span>
                        {showProductDropdown && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 100, maxHeight: 240, overflowY: 'auto', marginTop: 4 }}>
                                {filteredProducts.length === 0 ? (
                                    <div style={{ padding: '12px 14px', color: '#999', fontSize: 13 }}>{t('orderForm.noProductsInDropdown')}</div>
                                ) : filteredProducts.slice(0, 30).map(p => (
                                    <div key={p.id} onMouseDown={() => addProduct(p)}
                                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f8f9ff'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{p.name}</div>
                                            {getProductSku(p) && <div style={{ fontSize: 11, color: '#999' }}>{getProductSku(p)}</div>}
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gc-primary)' }}>{formatPrice(getProductSellingPrice(p))}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button onClick={() => setShowCustomItemForm(v => !v)}
                        style={{ padding: '0 14px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: showCustomItemForm ? '#f8f9ff' : 'white', fontSize: 13, cursor: 'pointer', color: 'var(--gc-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {t('orderForm.customItemBtn')}
                    </button>
                </div>

                {showCustomItemForm && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                        <input
                            style={{ ...inputStyle, flex: 2, minWidth: 140 }}
                            value={customItemName}
                            onChange={e => setCustomItemName(e.target.value)}
                            placeholder={t('orderForm.customItemNamePh')}
                        />
                        <div style={{ flex: 1, minWidth: 100 }}>
                            <input
                                style={inputStyle}
                                value={customItemPrice}
                                {...moneyInputHandlers('customItem', customItemPrice, setCustomItemPrice)}
                                placeholder={t('orderForm.customItemPricePh')}
                                aria-label={t('orderForm.customItemPricePh')}
                            />
                            {moneyError('customItem')}
                        </div>
                        <button onClick={addCustomItem} disabled={!customItemName.trim() || !currencyResolved}
                            style={{ padding: '0 16px', border: 'none', borderRadius: 8, background: customItemName.trim() ? 'var(--gc-primary)' : '#c7cbe8', color: 'white', fontSize: 13, fontWeight: 600, cursor: customItemName.trim() ? 'pointer' : 'not-allowed' }}>
                            {t('orderForm.customItemAdd')}
                        </button>
                    </div>
                )}

                {/* Line items list */}
                {lineItems.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13 }}>{t('orderForm.noItemsYet')}</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {lineItems.map(item => (
                            <div key={item._id} style={{ border: '0.5px solid #e0e3f0', borderRadius: 10, padding: '12px 14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111', flex: 1 }}>
                                        {item.name}
                                        {!item.product_id && (
                                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#999', border: '0.5px solid #e0e3f0', borderRadius: 4, padding: '1px 5px' }}>
                                                {t('orderForm.customItemTag')}
                                            </span>
                                        )}
                                    </div>
                                    <button onClick={() => removeItem(item._id)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, padding: '0 4px', marginLeft: 8 }}>×</button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <button onClick={() => updateQty(item._id, -1)}
                                            style={{ width: 28, height: 28, border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                                        <input type="number" min="1" value={item.qty} onChange={e => setQty(item._id, e.target.value)}
                                            style={{ width: 48, textAlign: 'center', padding: '4px', border: '0.5px solid #e0e3f0', borderRadius: 6, fontSize: 13, outline: 'none' }} />
                                        <button onClick={() => updateQty(item._id, 1)}
                                            style={{ width: 28, height: 28, border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gc-primary)' }}>{formatPrice(item.unit_price * item.qty)}</div>
                                </div>
                                <input value={item.note} onChange={e => setItemNote(item._id, e.target.value)}
                                    placeholder={t('orderForm.itemNotePh')}
                                    style={{ ...inputStyle, marginTop: 8, fontSize: 12, padding: '6px 10px' }} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pricing */}
            <div style={sectionStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('orderForm.pricing')}</div>
                <div style={rowStyle}>
                    <div>
                        <label style={labelStyle}>{t('orderForm.discountType')}</label>
                        <select value={discountType} onChange={e => setDiscountType(e.target.value)} style={inputStyle}>
                            <option value="none">{t('orderForm.noDiscount')}</option>
                            <option value="percent">{t('orderForm.discountPercent')}</option>
                            <option value="fixed">{t('orderForm.discountFixed', { currency: currencyLabel })}</option>
                        </select>
                    </div>
                    {discountType !== 'none' && (
                        <div>
                            <label htmlFor="order-discount" style={labelStyle}>{discountType === 'percent' ? t('orderForm.discountPctLabel') : t('orderForm.discountAmtLabel', { currency: currencyLabel })}</label>
                            <input
                                id="order-discount"
                                style={inputStyle}
                                value={discountValue}
                                {...(discountType === 'fixed'
                                    ? moneyInputHandlers('discount', discountValue, setDiscountValue)
                                    : { onChange: e => setDiscountValue(normalizePercentInput(e.target.value)), inputMode: 'decimal' })}
                                placeholder={discountType === 'percent' ? '10' : (hasDecimals ? '0.00' : '0')} />
                            {discountType === 'fixed' && moneyError('discount')}
                        </div>
                    )}
                </div>
                <div style={rowStyle}>
                    <div>
                        <label style={labelStyle}>{t('orderForm.taxPct')}</label>
                        <input
                            style={inputStyle}
                            value={taxPercent}
                            onChange={e => setTaxPercent(normalizePercentInput(e.target.value))}
                            placeholder={t('orderForm.taxPh')}
                            inputMode="decimal"
                        />
                    </div>
                    <div>
                        <label htmlFor="order-shipping" style={labelStyle}>{t('orderForm.shippingFee', { currency: currencyLabel })}</label>
                        <input
                            id="order-shipping"
                            style={inputStyle}
                            value={shippingFee}
                            {...moneyInputHandlers('shipping', shippingFee, setShippingFee)}
                            placeholder={hasDecimals ? '0.00' : '0'}
                        />
                        {moneyError('shipping')}
                    </div>
                </div>

                {/* Summary */}
                <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 14, marginTop: 4 }}>
                    {[
                        { label: t('orders.subtotal'), value: subtotal },
                        discountAmt > 0 && { label: t('orders.discount'), value: -discountAmt },
                        taxAmt > 0 && { label: t('orderForm.taxSummary', { pct: taxPercent }), value: taxAmt },
                        shipping > 0 && { label: t('orderForm.shippingSummary'), value: shipping },
                    ].filter(Boolean).map(row => (
                        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#555', marginBottom: 6 }}>
                            <span>{row.label}</span>
                            <span style={{ color: row.value < 0 ? '#dc2626' : '#333' }}>{formatPrice(Math.abs(row.value))}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: '#111', marginTop: 8, paddingTop: 8, borderTop: '1px solid #e0e3f0' }}>
                        <span>{t('orderForm.total')}</span>
                        <span style={{ color: 'var(--gc-primary)' }}>{formatPrice(finalTotal)}</span>
                    </div>
                </div>
            </div>

            {/* Payment */}
            <div style={sectionStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('orderForm.payment')}</div>
                <div style={rowStyle}>
                    <div>
                        <label style={labelStyle}>{t('orderForm.paymentMethod')}</label>
                        <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={inputStyle}>
                            {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>{t('orderForm.paymentStatus')}</label>
                        <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)} style={inputStyle}>
                            <option value="unpaid">{t('orderForm.unpaid')}</option>
                            <option value="deposit_paid">{t('orderForm.depositPaid')}</option>
                            <option value="paid">{t('orderForm.paid')}</option>
                        </select>
                    </div>
                </div>
                {(paymentStatus === 'deposit_paid' || paymentStatus === 'partial') && (
                    <div>
                        <label htmlFor="order-deposit" style={labelStyle}>{t('orderForm.depositAmount', { currency: currencyLabel })}</label>
                        <input
                            id="order-deposit"
                            style={inputStyle}
                            value={depositAmount}
                            {...moneyInputHandlers('deposit', depositAmount, setDepositAmount)}
                            placeholder={t('orderForm.depositPh')}
                        />
                        {moneyError('deposit')}
                    </div>
                )}
            </div>

            {/* Notes */}
            <div style={sectionStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('orders.internalNotes')}</div>
                <textarea value={internalNote} onChange={e => setInternalNote(e.target.value)}
                    placeholder={t('orderForm.internalNotesPh')}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            {/* Submit */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingBottom: isDesktop ? 0 : 20 }}>
                <button onClick={() => navigate(`${prefix}/orders`)}
                    style={{ padding: '10px 22px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 14, cursor: 'pointer', color: '#333' }}>
                    {t('common.cancel')}
                </button>
                <button onClick={handleSubmit} disabled={saving || !currencyResolved}
                    style={{ padding: '10px 24px', background: saving ? '#8896e8' : 'var(--gc-primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
                    {saving ? t('orderForm.creating') : t('orderForm.createOrder')}
                </button>
            </div>
        </div>
    )

    return isDesktop
        ? <DesktopShell>{content}</DesktopShell>
        : <AppShell activeTab="orders">{content}</AppShell>
}
